import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import {
  CreateLicenseTypeFailure,
  CreateLicenseTypeRequest,
  CreateLicenseTypeSuccess,
  DeleteLicenseTypeFailure,
  DeleteLicenseTypeRequest,
  DeleteLicenseTypeSuccess,
  FetchLicenseTypeFailure,
  FetchLicenseTypeRequest,
  FetchLicenseTypeSuccess,
  FetchLicenseTypesRequest,
  FetchLicenseTypesSuccess,
  FetchLicenseTypesFailure,
  UpdateLicenseTypeFailure,
  UpdateLicenseTypeRequest,
  UpdateLicenseTypeSuccess,
  FetchDefaultPrefixRequest,
  FetchDefaultPrefixSuccess,
  FetchDefaultPrefixFailure,
} from './license-types.actions'
import { LicenseTypesService } from './license-types.service'
import * as LicenseTypes from './license-types.types'

@Injectable()
export class LicenseTypesEffects {
  constructor(
    private actions: Actions,
    private licenseTypesService: LicenseTypesService,
    private router: Router,
  ) {}

  FetchLicenseTypes$ = createEffect(() =>
    this.actions.pipe(
      ofType(LicenseTypes.FETCH_LICENSE_TYPES_REQUEST),
      switchMap((action: FetchLicenseTypesRequest) => {
        return this.licenseTypesService.fetchLicenseTypes(action.payload).pipe(
          map((result) => {
            return new FetchLicenseTypesSuccess(
              result.data.results,
              result.data.totalCount,
              result.data.filteredCount,
            )
          }),
          catchError((err, caught) => {
            return of(new FetchLicenseTypesFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchLicenseType$ = createEffect(() =>
    this.actions.pipe(
      ofType(LicenseTypes.FETCH_LICENSE_TYPE_REQUEST),
      switchMap((action: FetchLicenseTypeRequest) => {
        return this.licenseTypesService.fetchLicenseType(action.id).pipe(
          map((result) => {
            return new FetchLicenseTypeSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchLicenseTypeFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DeleteLicenseType$ = createEffect(() =>
    this.actions.pipe(
      ofType(LicenseTypes.DELETE_LICENSE_TYPE_REQUEST),
      switchMap((action: DeleteLicenseTypeRequest) => {
        return this.licenseTypesService.deleteLicenseType(action.id).pipe(
          switchMap(() => {
            return [new DeleteLicenseTypeSuccess()]
          }),
          catchError((err, caught) => {
            return of(new DeleteLicenseTypeFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  CreateLicenseType$ = createEffect(() =>
    this.actions.pipe(
      ofType(LicenseTypes.CREATE_LICENSE_TYPE_REQUEST),
      switchMap((action: CreateLicenseTypeRequest) => {
        return this.licenseTypesService.createLicenseType(action.payload).pipe(
          switchMap(() => {
            this.router.navigate(['/admin/license-types'])
            return [new CreateLicenseTypeSuccess()]
          }),
          catchError(() => {
            return of(new CreateLicenseTypeFailure({}))
          }),
        )
      }),
    ),
  )

  UpdateLicenseType$ = createEffect(() =>
    this.actions.pipe(
      ofType(LicenseTypes.UPDATE_LICENSE_TYPE_REQUEST),
      switchMap((action: UpdateLicenseTypeRequest) => {
        return this.licenseTypesService
          .updateLicenseType(action.id, action.payload)
          .pipe(
            switchMap(() => {
              this.router.navigate(['/admin/license-types'])
              return [new UpdateLicenseTypeSuccess()]
            }),
            catchError(() => {
              return of(new UpdateLicenseTypeFailure({}))
            }),
          )
      }),
    ),
  )

  FetchDefaultPrefix$ = createEffect(() =>
    this.actions.pipe(
      ofType(LicenseTypes.FETCH_DEFAULT_PREFIX_REQUEST),
      switchMap((action: FetchDefaultPrefixRequest) => {
        return this.licenseTypesService.fetchDefaultPrefix().pipe(
          map((result) => {
            return new FetchDefaultPrefixSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchDefaultPrefixFailure({ error: err }))
          }),
        )
      }),
    ),
  )
}
